import { useEffect, useState } from 'react';
import { useAuth0, useUserAuth, handleLogout, setTokenRefresher } from '@lucid/auth';
import { useQuery } from '@tanstack/react-query';
import { usePortalStore } from '../store/portal-store';
import { usePortalConfig } from '../context/PortalCoreContext';
import type { UserIdentity } from '../types/user-identity';

const USER_IDENTITY_STORAGE_KEY = 'lupp-user-identity';

const buildStorageKey = (userId: string): string => `${USER_IDENTITY_STORAGE_KEY}-${userId}`;

const getItem = <T>(key: string): T | null => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

const setItem = (key: string, value: unknown): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore storage errors
  }
};

const removeItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

const isAuthError = (error: unknown): boolean => {
  if (error && typeof error === 'object' && 'status' in error) {
    const s = (error as { status?: number }).status;
    return s === 401 || s === 403;
  }
  return false;
};

/**
 * Syncs the authenticated user's identity from the /me endpoint into the portal store.
 *
 * - Registers a token refresher so API interceptors can fetch fresh ID tokens.
 * - Sets locale and language from the JWT locale claim on first login.
 * - Caches the /me response in localStorage to avoid duplicate API calls.
 * - Handles identity-fetch errors by clearing state and redirecting to the login page
 *   (401/403 with reason=invalid-user, other failures with reason=unavailable).
 *
 * Must be rendered inside PortalCoreProvider (needs config context).
 */
export const useUserIdentitySync = () => {
  const { rolesNamespace, fetchIdentity, defaultLocale } = usePortalConfig();
  const { user, isLoading: isAuthLoading, accessToken } = useUserAuth(rolesNamespace);
  const { logout, getAccessTokenSilently, getIdTokenClaims, isLoading: auth0Loading } = useAuth0();
  const {
    setUserIdentity,
    clearUserIdentity,
    userIdentity: storedIdentity,
    setLocale,
    setLanguage,
  } = usePortalStore();

  const [hasCheckedCache, setHasCheckedCache] = useState(false);
  const [hasSetTokenLocale, setHasSetTokenLocale] = useState(false);

  const userId = user?.sub as string | undefined;
  const tokenLocale = (user?.locale as string | undefined)?.toLowerCase();

  // Register ID token refresher so API interceptors can obtain fresh tokens.
  useEffect(() => {
    if (!user?.sub) return;
    setTokenRefresher(async () => {
      await getAccessTokenSilently({ cacheMode: 'on' });
      const claims = await getIdTokenClaims();
      return claims?.__raw ?? '';
    });
  }, [user?.sub, getAccessTokenSilently, getIdTokenClaims]);

  // Set locale from JWT claim once per session (only if store still has the default).
  useEffect(() => {
    if (user?.sub && !hasSetTokenLocale) {
      const resolved = tokenLocale || defaultLocale;
      const current = usePortalStore.getState().locale;
      if (current === defaultLocale) {
        setLocale(resolved);
      }
      setHasSetTokenLocale(true);
    }
  }, [user?.sub, tokenLocale, hasSetTokenLocale, defaultLocale, setLocale, setLanguage]);

  // Hydrate from localStorage cache on first mount.
  useEffect(() => {
    if (userId && !storedIdentity && !hasCheckedCache) {
      const cached = getItem<UserIdentity>(buildStorageKey(userId));
      if (cached) setUserIdentity(cached);
      setHasCheckedCache(true);
    }
  }, [userId, storedIdentity, hasCheckedCache, setUserIdentity]);

  const isReadyForApiCall =
    !isAuthLoading && !!userId && !!accessToken && !storedIdentity && hasCheckedCache;

  const {
    data: fetchedIdentity,
    isLoading,
    error,
    isSuccess,
  } = useQuery({
    queryKey: ['portal-core-user-identity', userId],
    queryFn: fetchIdentity,
    enabled: isReadyForApiCall,
    retry: (failureCount, err) => {
      if (isAuthError(err)) return false;
      return failureCount < 1;
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Persist to store and localStorage when successfully fetched.
  useEffect(() => {
    if (isSuccess && fetchedIdentity && userId) {
      setUserIdentity(fetchedIdentity);
      setItem(buildStorageKey(userId), fetchedIdentity);
      const preferredLanguage =
        fetchedIdentity.preferredLanguage?.split(/[-_]/)[0].toLowerCase() ?? 'en';
      setLanguage(preferredLanguage);
    }
  }, [isSuccess, fetchedIdentity, userId, setUserIdentity, setLanguage]);

  // Clear identity on logout.
  // Guard with auth0Loading to avoid wiping persisted identity during Auth0's
  // initialization window — useUserAuth briefly reports isAuthLoading=false while
  // Auth0 is still resolving its session (isAuthenticated=false transiently).
  useEffect(() => {
    if (!user && !isAuthLoading && !auth0Loading) {
      clearUserIdentity();
      if (userId) removeItem(buildStorageKey(userId));
      setHasCheckedCache(false);
    }
  }, [user, isAuthLoading, auth0Loading, userId, clearUserIdentity]);

  // Handle identity-fetch errors — clear state, log out, and redirect to login
  // with a reason the login page can surface to the user. Auth failures (401/403)
  // indicate an invalid user; any other failure means the identity service is
  // unavailable and the user should retry or contact support.
  useEffect(() => {
    if (error && userId) {
      console.error('Failed to fetch user identity:', error);
      removeItem(buildStorageKey(userId));
      clearUserIdentity();
      const redirectTo = isAuthError(error)
        ? `${window.location.origin}/login?error=auth&reason=invalid-user`
        : `${window.location.origin}/login?error=identity&reason=unavailable`;
      handleLogout(logout, redirectTo);
    }
  }, [error, userId, clearUserIdentity, logout]);

  return {
    userIdentity: storedIdentity ?? fetchedIdentity ?? null,
    isLoading,
    error,
    isSuccess,
  };
};
